'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function GradeCalculator() {
  const t = useTranslations("tools.gradeCalculator");

  const [inputs, setInputs] = useState({ assignment: '', grade: 0, weight: 0 });
  const [results, setResults] = useState<{ currentGrade: number; finalGradeNeeded: string } | null>(null);
  const [screenSize, setScreenSize] = useState('mobile');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const { grade, weight } = inputs;

    if (isNaN(grade) || isNaN(weight) || weight <= 0) {
      alert(t("invalidInput"));
      return;
    }

    const currentGrade = (grade * weight) / 100;
    const finalGradeGoal = 90; // Example target grade
    const remainingWeight = 100 - weight;
    const finalGradeNeeded = ((finalGradeGoal - currentGrade) / (remainingWeight / 100)).toFixed(2);
    const finalGradeNeededValue = parseFloat(finalGradeNeeded);

    setResults({
      currentGrade: currentGrade,
      finalGradeNeeded: finalGradeNeededValue > 0 ? finalGradeNeeded : t("alreadyAchieved"),
    });
  };

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', padding: '1rem' },
    { padding: '1rem' }, // mobile
    { padding: '2rem' }, // tablet
    { padding: '3rem' }  // desktop
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: '#2d3748', marginBottom: '1rem' },
    { fontSize: '2rem' }, // mobile
    { fontSize: '2.5rem' }, // tablet
    { fontSize: '3rem' }   // desktop
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      padding: '1.5rem 1rem',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' }, // mobile
    { padding: '2rem 1.5rem' }, // tablet
    { padding: '3rem 2rem' }    // desktop
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      padding: '0.75rem 1rem',
      fontSize: '1rem',
      border: '2px solid rgba(255, 90, 112, 0.2)',
      borderRadius: '0.5rem',
      marginBottom: '1rem',
    },
    { padding: '0.75rem 1rem' }, // mobile
    { padding: '0.875rem 1.25rem' }, // tablet
    { padding: '1rem 1.5rem' }    // desktop
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      padding: '0.875rem 1.5rem',
      fontSize: '1rem',
      fontWeight: 700,
      color: '#fff',
      background: 'linear-gradient(135deg, #ff5a70 0%, #ffc400 100%)',
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    { padding: '0.875rem 1.5rem' }, // mobile
    { padding: '1rem 2rem' }, // tablet
    { padding: '1.25rem 2.5rem' } // desktop
  );

  const resultsCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.9)',
      padding: '1rem',
      borderRadius: '0.75rem',
      marginTop: '1rem',
      border: '1px solid rgba(255, 90, 112, 0.1)',
    },
    { padding: '1rem' }, // mobile
    { padding: '1.5rem' }, // tablet
    { padding: '2rem' }    // desktop
  );

  return (
    <div style={containerStyle}>
      <header style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div>
          <h2>{t("toolTitle")}</h2>
          <form onSubmit={handleCalculate}>
            <div>
              <label>{t("inputLabel1")}</label>
              <input
                type="text"
                value={inputs.assignment}
                onChange={(e) => setInputs({ ...inputs, assignment: e.target.value })}
                style={inputStyle}
                required
              />
            </div>
            <div>
              <label>{t("inputLabel2")}</label>
              <input
                type="number"
                value={inputs.grade}
                onChange={(e) => setInputs({ ...inputs, grade: parseFloat(e.target.value) })}
                style={inputStyle}
                min="0"
                max="100"
                required
              />
            </div>
            <div>
              <label>{t("inputLabel3")}</label>
              <input
                type="number"
                value={inputs.weight}
                onChange={(e) => setInputs({ ...inputs, weight: parseFloat(e.target.value) })}
                style={inputStyle}
                min="0"
                required
              />
            </div>
            <button type="submit" style={calculateButtonStyle}>{t("calculateButton")}</button>
          </form>
          {results && (
            <div style={resultsCardStyle}>
              <h3>{t("resultsTitle")}</h3>
              <div>
                <span>{t("currentGradeLabel")}: </span>
                <span>{results.currentGrade.toFixed(2)}</span>
              </div>
              <div>
                <span>{t("finalGradeNeededLabel")}: </span>
                <span>{results.finalGradeNeeded}</span>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={{ marginBottom: '2rem', background: 'rgba(255, 255, 255, 0.6)', padding: '2rem', borderRadius: '1rem' }}>
        <h2>{t("introTitle")}</h2>
        <p>{t("introParagraph")}</p>
      </section>

      <section style={{ marginBottom: '2rem', background: 'rgba(255, 255, 255, 0.6)', padding: '2rem', borderRadius: '1rem' }}>
        <h2>{t("howToUseTitle")}</h2>
        <ol>
          <li>{t("howToUseStep1")}</li>
          <li>{t("howToUseStep2")}</li>
          <li>{t("howToUseStep3")}</li>
        </ol>
      </section>

      <section style={{ marginBottom: '2rem', background: 'rgba(255, 255, 255, 0.6)', padding: '2rem', borderRadius: '1rem' }}>
        <h2>{t("insightsTitle")}</h2>
        <p>{t("insightsContent")}</p>
      </section>

      <section style={{ marginBottom: '2rem', background: 'rgba(255, 255, 255, 0.6)', padding: '2rem', borderRadius: '1rem' }}>
        <h2>{t("detailsTitle")}</h2>
        <p>{t("detailsContent")}</p>
      </section>

      <section style={{ marginBottom: '2rem', background: 'rgba(255, 255, 255, 0.6)', padding: '2rem', borderRadius: '1rem' }}>
        <h2>{t("faqTitle")}</h2>
        <p>{t("faqContent")}</p>
      </section>

      <section style={{ marginBottom: '2rem', background: 'rgba(255, 255, 255, 0.6)', padding: '2rem', borderRadius: '1rem' }}>
        <h2>{t("referencesTitle")}</h2>
        <ul>
          <li><a href="https://www.graphcalc.com/final-exam-grade-calculator/" target="_blank" rel="noopener noreferrer">Final Exam Grade Calculator</a></li>
          <li><a href="https://gradecalculator.app/about/" target="_blank" rel="noopener noreferrer">Learn More About Our Grade Calculator</a></li>
          <li><a href="https://hackmd.io/@hIVrxnbrTt6aHjbblRpS-w/HkjdYkhW2" target="_blank" rel="noopener noreferrer">The Advantages Of Using A Grade Calculator For Students And Teachers</a></li>
        </ul>
      </section>
    </div>
  );
}
